import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { format, formatDistanceToNow } from "date-fns";
import { EnvelopeOpen, Cursor, Warning, CheckCircle, PaperPlaneTilt, Eye, XCircle } from "@phosphor-icons/react";

const ICONS = {
    sent: PaperPlaneTilt,
    delivered: CheckCircle,
    opened: EnvelopeOpen,
    reopened: Eye,
    clicked: Cursor,
    bounced: Warning,
    failed: XCircle,
    unsubscribed: XCircle,
};

const COLORS = {
    sent: "text-slate-300",
    delivered: "text-emerald-300",
    opened: "text-cyan-300",
    reopened: "text-blue-300",
    clicked: "text-orange-300",
    bounced: "text-red-300",
    failed: "text-red-400",
    unsubscribed: "text-slate-400",
};

export default function AdminActivity() {
    const [items, setItems] = useState([]);

    const load = useCallback(() => api.get("/admin/activity").then((r) => setItems(r.data.items)), []);
    useEffect(() => {
        document.title = "Surveys2CAD | Activity";
        load();
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
    }, [load]);

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-activity">
            <div className="mb-8">
                <div className="overline mb-2">Real-time</div>
                <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 tracking-tight">Activity Feed</h1>
                <p className="text-slate-400 mt-2">
                    Live stream of opens, clicks, bounces, re-opens and unsubscribes — refreshes every 4s.
                </p>
            </div>

            <div className="tech-card overflow-hidden">
                {items.length === 0 ? (
                    <div className="p-10 text-center text-slate-500">
                        No activity yet. Send a campaign to start seeing events stream here.
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-800" data-testid="activity-list">
                        {items.map((e) => {
                            const Icon = ICONS[e.event_type] || EnvelopeOpen;
                            return (
                                <li key={e.id} className="p-4 flex items-center gap-4 hover:bg-slate-900/40">
                                    <Icon size={22} weight="duotone" className={COLORS[e.event_type] || "text-slate-400"} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm">
                                            <span className={`font-heading font-semibold capitalize ${COLORS[e.event_type]}`}>{e.event_type}</span>
                                            <span className="text-slate-400"> · {e.email || "unknown"}</span>
                                        </div>
                                        {e.meta?.url && (
                                            <div className="text-xs text-slate-500 font-mono-alt truncate mt-0.5">→ {e.meta.url}</div>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 font-mono-alt whitespace-nowrap text-right">
                                        <div>{e.timestamp ? formatDistanceToNow(new Date(e.timestamp), { addSuffix: true }) : "—"}</div>
                                        <div className="text-[10px] opacity-60">{e.timestamp ? format(new Date(e.timestamp), "MMM d HH:mm:ss") : ""}</div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
